/**
 * useCollaboration — Collaboration utilities for shared workspaces.
 *
 * Provides:
 * - Changelog entry writing when the local user saves changes
 * - Collaboration state info (whether collaboration is active)
 *
 * NOTE: Real-time node/connection sync is handled directly in App.tsx via
 * subscribeToProject(), which already manages echo suppression and state
 * updates. This hook does NOT create a duplicate subscription — it only
 * provides the changelog/audit-trail side of collaboration.
 */

import { useMemo, useCallback } from 'react';
import { CanvasNode, Connection, UserProfile } from '../types';
import { writeChangelogEntry } from '../utils/storage';

export interface CollaborationState {
  isListening: boolean;
}

export interface UseCollaborationResult {
  collaborationState: CollaborationState;
  onLocalSave: (changes: LocalSaveChanges) => void;
}

export interface LocalSaveChanges {
  addedNodes?: CanvasNode[];
  updatedNodes?: CanvasNode[];
  deletedNodeIds?: string[];
  addedConnections?: Connection[];
  deletedConnectionIds?: string[];
  renamed?: string;
}

export function useCollaboration(
  projectId: string | null,
  user: UserProfile | null,
  enabled: boolean = true,
): UseCollaborationResult {
  // Worked out from what is passed in, not kept in state: setting it after every open rendered the whole app once more
  const isListening = !!(projectId && enabled && user);
  const collaborationState = useMemo<CollaborationState>(() => ({ isListening }), [isListening]);

  const onLocalSave = useCallback(
    (changes: LocalSaveChanges) => {
      if (!projectId || !user) return;

      const writeEntry = (action: string, nodeId?: string, nodeType?: string, details?: string) => {
        writeChangelogEntry(projectId, {
          userId: user.sub,
          userEmail: user.email,
          userName: user.name,
          action,
          nodeId,
          nodeType,
          details,
        }).catch(() => {});
      };

      if (changes.addedNodes) {
        for (const node of changes.addedNodes) {
          writeEntry('node_added', node.id, node.type, node.data.title);
        }
      }
      if (changes.updatedNodes) {
        for (const node of changes.updatedNodes) {
          writeEntry('node_updated', node.id, node.type, node.data.title);
        }
      }
      if (changes.deletedNodeIds) {
        for (const nodeId of changes.deletedNodeIds) {
          writeEntry('node_deleted', nodeId);
        }
      }
      if (changes.addedConnections) {
        for (const conn of changes.addedConnections) {
          writeEntry('connection_added', conn.id, undefined, `${conn.sourceId} → ${conn.targetId}`);
        }
      }
      if (changes.deletedConnectionIds) {
        for (const connId of changes.deletedConnectionIds) {
          writeEntry('connection_deleted', connId);
        }
      }
      if (changes.renamed) {
        writeEntry('project_renamed', undefined, undefined, changes.renamed);
      }
    },
    [projectId, user],
  );

  return {
    collaborationState,
    onLocalSave,
  };
}
